import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../utils/icons';
import { getSeverityIcon, getRecIcon, formatTimestamp, formatCost } from '../../utils/aiHelpers';
import './AIWeatherAgent.css';

const API_URL = process.env.REACT_APP_API_URL || '';

const UI_STATE_KEY = 'weatherDashboard_uiState';

function AIWeatherAgent({ activeLocation, weatherData, onStatusChange }) {
  const [expanded, setExpanded] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(UI_STATE_KEY) || '{}');
      return saved.aiExpanded === true;
    } catch { return false; }
  });

  const toggleExpanded = () => {
    setExpanded(prev => {
      const next = !prev;
      try {
        const current = JSON.parse(localStorage.getItem(UI_STATE_KEY) || '{}');
        current.aiExpanded = next;
        localStorage.setItem(UI_STATE_KEY, JSON.stringify(current));
      } catch { /* ignore */ }
      return next;
    });
  };
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [agentStatus, setAgentStatus] = useState('checking'); // healthy | degraded | fallback | budget_exceeded | error | checking
  const [costInfo, setCostInfo] = useState(null);

  // Check AI agent health on mount
  useEffect(() => {
    const checkHealth = async () => {
      try {
        const res = await fetch(`${API_URL}/api/ai/health`);
        if (res.ok) {
          const data = await res.json();
          setAgentStatus(data.status); // "healthy", "fallback", or "budget_exceeded"
          if (data.budget) {
            setCostInfo(data.budget);
          }
        } else {
          setAgentStatus('fallback');
        }
      } catch {
        setAgentStatus('fallback');
      }
    };
    checkHealth();
  }, []);

  // Fetch AI analysis when location changes
  const fetchAnalysis = useCallback(async () => {
    if (!activeLocation) return;

    console.log('[AIWeatherAgent DEBUG] Fetching analysis for:', activeLocation);
    setLoading(true);
    setError(null);

    try {
      const res = await fetch(`${API_URL}/api/ai/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ city: activeLocation }),
      });

      console.log('[AIWeatherAgent DEBUG] Response status:', res.status);

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || `HTTP ${res.status}`);
      }

      const data = await res.json();
      console.log('[AIWeatherAgent DEBUG] Full API response:', JSON.stringify(data, null, 2));
      console.log('[AIWeatherAgent DEBUG] Model used:', data.model);
      console.log('[AIWeatherAgent DEBUG] Alerts count:', data.alerts?.length || 0);
      console.log('[AIWeatherAgent DEBUG] Alerts:', data.alerts);
      console.log('[AIWeatherAgent DEBUG] Briefing:', data.briefing);
      console.log('[AIWeatherAgent DEBUG] Error message:', data.error_message);
      console.log('[AIWeatherAgent DEBUG] Budget exceeded:', data.budget_exceeded);
      setAnalysis(data);

      // Update cost info from response
      if (data.cost_info) {
        setCostInfo(data.cost_info);
      }

      // Update status based on model used
      if (data.budget_exceeded) {
        setAgentStatus('budget_exceeded');
        onStatusChange && onStatusChange('fallback');
      } else if (data.model === 'rule-based-fallback') {
        // Distinguish between degraded (key exists but errors) and plain fallback
        setAgentStatus(data.error_message ? 'degraded' : 'fallback');
        onStatusChange && onStatusChange('fallback');
      } else {
        setAgentStatus('healthy');
        onStatusChange && onStatusChange('success');
      }
    } catch (err) {
      setError(err.message);
      setAnalysis(null);
      onStatusChange && onStatusChange('fallback');
    } finally {
      setLoading(false);
    }
  }, [activeLocation]);

  // Auto-fetch when location changes
  useEffect(() => {
    if (activeLocation) {
      fetchAnalysis();
    }
  }, [activeLocation, fetchAnalysis]);

  const alertCount = analysis?.alerts?.length || 0;
  const criticalCount = analysis?.alerts?.filter(a => a.severity === 'critical').length || 0;

  const budgetPercent = costInfo
    ? Math.min(100, Math.round((costInfo.daily_spent / costInfo.daily_budget) * 100))
    : 0;

  return (
    <div className={`ai-agent-panel ${expanded ? 'expanded' : 'collapsed'}`}>
      {/* Header – always visible */}
      <div className="ai-agent-header" onClick={toggleExpanded}>
        <div className="ai-agent-header-left">
          <div className={`ai-agent-icon ${agentStatus === 'budget_exceeded' ? 'budget-warn' : ''}`}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 2a7 7 0 0 1 7 7c0 2.38-1.19 4.47-3 5.74V17a2 2 0 0 1-2 2H10a2 2 0 0 1-2-2v-2.26C6.19 13.47 5 11.38 5 9a7 7 0 0 1 7-7z"/>
              <line x1="10" y1="22" x2="14" y2="22"/>
              <line x1="9" y1="9" x2="15" y2="9"/>
              <line x1="12" y1="6" x2="12" y2="12"/>
            </svg>
          </div>
          <div className="ai-agent-title-group">
            <span className="ai-agent-title">AI Weather Agent</span>
            <span className="ai-agent-subtitle">
              <span className={`ai-status-dot ${agentStatus === 'budget_exceeded' ? 'warning' : agentStatus}`}></span>
              {agentStatus === 'healthy' ? 'Bedrock Connected' :
               agentStatus === 'degraded' ? '⚠️ Bedrock Errors - Rule-Based Mode' :
               agentStatus === 'budget_exceeded' ? 'Budget Limit Reached' :
               agentStatus === 'fallback' ? 'Rule-Based Mode' :
               agentStatus === 'checking' ? 'Connecting...' : 'Offline'}
            </span>
          </div>
        </div>

        <div className="ai-agent-header-right">
          {!expanded && analysis && (
            <>
              {alertCount > 0 ? (
                <span className="ai-collapsed-alert-count has-alerts">
                  <Icon name="alertTriangle" size={10} /> {alertCount} alert{alertCount !== 1 ? 's' : ''}
                </span>
              ) : (
                <span className="ai-collapsed-alert-count no-alerts">
                  ✓ Clear
                </span>
              )}
            </>
          )}
          <button
            className={`ai-refresh-btn ${loading ? 'loading' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              fetchAnalysis();
            }}
            disabled={loading || !activeLocation}
            title="Refresh analysis"
          >
            <Icon name="refresh" size={12} />
          </button>
          <span className="ai-agent-toggle">
            <Icon name="arrowDown" size={14} />
          </span>
        </div>
      </div>

      {/* Collapsed preview */}
      {!expanded && analysis && (
        <div className="ai-collapsed-preview">
          <span className="ai-collapsed-briefing">{analysis.briefing}</span>
        </div>
      )}

      {/* Expanded body */}
      <div className="ai-agent-body">
        {loading && !analysis ? (
          <div className="ai-loading">
            <div className="ai-loading-dots">
              <span></span><span></span><span></span>
            </div>
            Analyzing weather conditions...
          </div>
        ) : error && !analysis ? (
          <div className="ai-error">
            <Icon name="alertTriangle" size={14} />
            <span>{error}</span>
          </div>
        ) : analysis ? (
          <>
            {/* Briefing */}
            <div className="ai-briefing">
              <div className="ai-briefing-label">Weather Briefing</div>
              {analysis.briefing}
            </div>

            {/* Alerts */}
            <div className="ai-alerts">
              <div className="ai-alerts-label">
                Alerts {alertCount > 0 && `(${alertCount})`}
              </div>
              {alertCount > 0 ? (
                analysis.alerts.map((alert, i) => (
                  <div key={i} className={`ai-alert-item ${alert.severity}`}>
                    <span className="ai-alert-icon">{getSeverityIcon(alert.severity)}</span>
                    <div className="ai-alert-content">
                      <div className="ai-alert-title">{alert.title}</div>
                      <div className="ai-alert-message">{alert.message}</div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="ai-no-alerts">
                  ✅ No weather alerts – conditions look good!
                </div>
              )}
            </div>

            {/* Recommendations */}
            {analysis.recommendations && analysis.recommendations.length > 0 && (
              <div className="ai-recommendations">
                <div className="ai-recommendations-label">Recommendations</div>
                <div className="ai-rec-list">
                  {analysis.recommendations.map((rec, i) => (
                    <span key={i} className="ai-rec-item">
                      <span className="rec-icon">{getRecIcon(rec)}</span>
                      {rec}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Cost / Budget info */}
            {costInfo && (
              <div className="ai-cost-section">
                <div className="ai-cost-header">
                  <span className="ai-cost-label">Daily Budget</span>
                  <span className="ai-cost-value">
                    {formatCost(costInfo.daily_spent)} / {formatCost(costInfo.daily_budget)}
                  </span>
                </div>
                <div className="ai-cost-bar-bg">
                  <div
                    className={`ai-cost-bar-fill ${budgetPercent >= 90 ? 'critical' : budgetPercent >= 70 ? 'warning' : ''}`}
                    style={{ width: `${budgetPercent}%` }}
                  />
                </div>
                <div className="ai-cost-details">
                  <span>{costInfo.daily_requests || 0} requests today</span>
                  <span>Monthly: {formatCost(costInfo.monthly_spent)} / {formatCost(costInfo.monthly_budget)}</span>
                </div>
                {agentStatus === 'budget_exceeded' && (
                  <div className="ai-budget-warning">
                    ⚠️ Budget exceeded — using free rule-based analysis. Resets at midnight UTC.
                  </div>
                )}
              </div>
            )}

            {/* Fallback error notice */}
            {analysis.model === 'rule-based-fallback' && analysis.error_message && (
              <div className="ai-fallback-error">
                <span className="ai-fallback-error-icon">⚠️</span>
                <span className="ai-fallback-error-text">
                  Bedrock unavailable: {analysis.error_message}
                </span>
              </div>
            )}

            {/* Footer */}
            <div className="ai-agent-footer">
              <span>
                {analysis.model === 'rule-based-fallback'
                  ? 'Rule-based analysis'
                  : `Powered by ${analysis.model || 'Amazon Bedrock'}`}
              </span>
              <span>{formatTimestamp(analysis.timestamp)}</span>
            </div>
          </>
        ) : (
          <div className="ai-loading">
            Select a location to get AI weather analysis
          </div>
        )}
      </div>
    </div>
  );
}

export default AIWeatherAgent;
